import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contactsApi, tasksApi } from '@/utils/api';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import EmailModal from '@/components/EmailModal';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ConfidenceBadge';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Building2, Plus,
  ChevronRight, Pencil, Check, X as XIcon,
  MessageSquare, Loader2, Clock, CheckSquare, Tag, Zap
} from 'lucide-react';

import { BLUE, BLUE_D, INK, MUTE, FAINT, LINE, CARD_ALT, SERIF } from '@/styles/milk';
import { relTime } from '@/utils/time';

// ─── Inline editable contact field ────────────────────────────────────────────────────
function ContactField({ icon: Icon, value, onSave, placeholder, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const save = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (value || '')) onSave(trimmed);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-1.5">
        <Icon size={14} style={{ color: FAINT, flexShrink: 0 }} />
        <input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') cancel();
          }}
          placeholder={placeholder}
          className="flex-1 text-sm rounded-lg px-2.5 py-1 outline-none"
          style={{ background: CARD_ALT, border: `1px solid ${BLUE}`, color: INK }}
        />
        <button onClick={save} className="p-1 rounded" style={{ color: 'hsl(142,52%,34%)' }}><Check size={13} /></button>
        <button onClick={cancel} className="p-1 rounded" style={{ color: FAINT }}><XIcon size={13} /></button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDraft(value || ''); setEditing(true); }}
      className="flex items-center gap-2 py-1.5 w-full group text-left rounded-lg px-1 -mx-1"
      style={{ transition: 'background .15s' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(33,46,64,.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon size={14} style={{ color: value ? FAINT : 'rgba(154,152,143,.5)', flexShrink: 0 }} />
      <span className="text-sm flex-1 truncate" style={{ color: value ? MUTE : 'rgba(154,152,143,.7)', fontStyle: value ? 'normal' : 'italic' }}>
        {value || placeholder}
      </span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60 shrink-0" style={{ color: FAINT, transition: 'opacity .15s' }} />
    </button>
  );
}

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openTaskDrawer } = useTaskDrawer();
  const [contact, setContact] = useState(null);
  const [actionCards, setActionCards] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await contactsApi.getActionCards(id);
      setContact(res.data.contact);
      setActionCards(res.data.action_cards || []);
      setTasks(res.data.tasks || []);
    } catch {
      toast.error('Person not found');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (editingName) nameRef.current?.focus(); }, [editingName]);

  const updateField = async (field, value) => {
    try {
      const res = await contactsApi.update(id, { [field]: value });
      setContact(res.data);
      toast.success('Saved', { duration: 1200 });
    } catch {
      toast.error('Failed to save');
    }
  };

  const toggleTask = async (task) => {
    const newStatus = task.status === 'done' ? 'not_started' : 'done';
    try {
      const res = await tasksApi.update(task.id, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === task.id ? res.data : t));
      if (newStatus === 'done') toast.success('Task completed!', { duration: 1200 });
    } catch { toast.error('Failed to update task'); }
  };

  const openRecapEmail = () => {
    const card = actionCards[0]; // most recent
    if (!card) { toast.error('No conversation yet to recap'); return; }
    setEmailOpen(true);
  };

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    setSavingName(true);
    try {
      const res = await contactsApi.update(id, { name: nameDraft.trim() });
      setContact(res.data);
      setEditingName(false);
      toast.success('Saved', { duration: 1200 });
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="pagewrap">
        <Skeleton className="h-5 w-32 mb-8" style={{ background: 'rgba(33,46,64,.06)' }} />
        <div className="grid md:grid-cols-[220px_1fr] gap-8">
          <div className="space-y-3">
            <Skeleton className="w-14 h-14 rounded-full mx-auto" style={{ background: 'rgba(33,46,64,.06)' }} />
            <Skeleton className="h-5 w-3/4 mx-auto" style={{ background: 'rgba(33,46,64,.06)' }} />
            <Skeleton className="h-4 w-full" style={{ background: 'rgba(33,46,64,.06)' }} />
            <Skeleton className="h-4 w-full" style={{ background: 'rgba(33,46,64,.06)' }} />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" style={{ background: 'rgba(33,46,64,.06)' }} />
            <Skeleton className="h-20 w-full rounded-xl" style={{ background: 'rgba(33,46,64,.06)' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!contact) return null;

  const initials = contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const openTasks = tasks.filter(t => t.status !== 'done');
  const recentCard = actionCards[0] || null;
  const lastMet = recentCard?.created_at || contact.last_met || null;
  const rolledTags = Array.from(new Set(actionCards.flatMap(c => c.tags || []))).slice(0, 12);

  return (
    <div className="pagewrap" data-testid="contact-detail-page" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* Back nav */}
      <button
        onClick={() => navigate('/contacts')}
        className="flex items-center gap-1.5 text-sm mb-7"
        style={{ color: MUTE, transition: 'color .15s' }}
        onMouseEnter={e => { e.currentTarget.style.color = INK; }}
        onMouseLeave={e => { e.currentTarget.style.color = MUTE; }}
      >
        <ArrowLeft size={14} />
        Back to Contacts
      </button>

      <div className="grid md:grid-cols-[240px_1fr] gap-8 items-start">

        {/* ── Profile panel ── */}
        <div
          data-testid="contact-profile"
          className="rounded-3xl p-6"
          style={{ background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04)' }}
        >
          {/* Avatar + name */}
          <div className="flex flex-col items-center mb-5">
            <Avatar className="w-16 h-16 mb-3">
              <AvatarFallback
                className="text-xl"
                style={{ background: 'rgba(103,150,194,0.14)', color: BLUE_D, fontFamily: SERIF }}
              >
                {initials}
              </AvatarFallback>
            </Avatar>

            {/* Editable name */}
            {editingName ? (
              <div className="flex items-center gap-1.5 w-full">
                <input
                  ref={nameRef}
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') { setEditingName(false); }
                  }}
                  className="flex-1 text-center text-lg rounded-lg px-2 py-1 outline-none"
                  style={{ background: CARD_ALT, border: `1px solid ${BLUE}`, color: INK, fontFamily: SERIF }}
                />
                {savingName && <Loader2 size={13} className="animate-spin" style={{ color: FAINT }} />}
              </div>
            ) : (
              <button
                onClick={() => { setNameDraft(contact.name); setEditingName(true); }}
                className="group flex items-center gap-1.5 rounded-lg px-2 py-1"
                style={{ transition: 'background .15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(33,46,64,.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontFamily: SERIF, fontSize: '22px', color: INK, lineHeight: 1.1 }}>
                  {contact.name}
                </span>
                <Pencil size={12} className="opacity-0 group-hover:opacity-60" style={{ color: FAINT, transition: 'opacity .15s' }} />
              </button>
            )}
          </div>

          {/* Contact fields — all inline editable */}
          <div className="space-y-0.5 mb-5">
            <ContactField icon={Mail} value={contact.email} onSave={v => updateField('email', v)} placeholder="Add email" type="email" />
            <ContactField icon={Phone} value={contact.phone} onSave={v => updateField('phone', v)} placeholder="Add phone" type="tel" />
            <ContactField icon={Building2} value={contact.company} onSave={v => updateField('company', v)} placeholder="Add company" />
          </div>

          {/* Divider */}
          <div className="h-px mb-4" style={{ background: LINE }} />

          {/* Quick actions — act from the contact */}
          <div className="space-y-2" data-testid="contact-quick-actions">
            <button
              onClick={openRecapEmail}
              data-testid="contact-email-button"
              className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-3 rounded-xl text-white"
              style={{ background: BLUE, boxShadow: '0 6px 16px rgba(103,150,194,.28)', transition: 'background .15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = BLUE_D; }}
              onMouseLeave={e => { e.currentTarget.style.background = BLUE; }}
            >
              <Mail size={14} />Email recap
            </button>
            <div className="flex gap-2">
              {contact.phone ? (
                <a href={`tel:${contact.phone}`} className="flex-1" data-testid="contact-call-button">
                  <button
                    className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-3 rounded-xl"
                    style={{ border: `1px solid ${LINE}`, color: INK, transition: 'background .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = CARD_ALT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Phone size={14} />Call
                  </button>
                </a>
              ) : (
                <button
                  disabled
                  data-testid="contact-call-button"
                  title="Add a phone number to call"
                  className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-3 rounded-xl opacity-40 cursor-not-allowed"
                  style={{ border: `1px solid ${LINE}`, color: MUTE }}
                >
                  <Phone size={14} />Call
                </button>
              )}
              <Link to="/capture" className="flex-1">
                <button
                  data-testid="contact-new-action-button"
                  className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 px-3 rounded-xl border border-dashed"
                  style={{ borderColor: BLUE, color: BLUE_D, transition: 'background .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(103,150,194,.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Plus size={14} />New
                </button>
              </Link>
            </div>
          </div>

          {/* Stats — last met / conversations / open tasks */}
          <div className="mt-4 pt-4 space-y-2" style={{ borderTop: `1px solid ${LINE}` }} data-testid="contact-stats">
            <div className="flex items-center justify-between text-xs" style={{ color: MUTE }}>
              <span className="flex items-center gap-1.5"><Clock size={12} />Last met</span>
              <span className="font-semibold" style={{ color: INK }}>{lastMet ? relTime(lastMet) : '—'}</span>
            </div>
            <div className="flex items-center justify-between text-xs" style={{ color: MUTE }}>
              <span className="flex items-center gap-1.5"><MessageSquare size={12} />Conversations</span>
              <span className="font-semibold" style={{ color: INK }}>{actionCards.length}</span>
            </div>
            <div className="flex items-center justify-between text-xs" style={{ color: MUTE }}>
              <span className="flex items-center gap-1.5"><CheckSquare size={12} />Open tasks</span>
              <span className="font-semibold" style={{ color: openTasks.length ? BLUE_D : INK }}>{openTasks.length}</span>
            </div>
          </div>

          {/* Rolled-up tags */}
          {rolledTags.length > 0 && (
            <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${LINE}` }} data-testid="contact-tags">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: FAINT }}>
                <Tag size={11} />Topics
              </div>
              <div className="flex flex-wrap gap-1.5">
                {rolledTags.map(t => (
                  <span key={t} className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ background: 'rgba(103,150,194,0.10)', color: BLUE_D }}>{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Right column: open tasks + conversation history ── */}
        <div>
          {/* Open tasks */}
          {openTasks.length > 0 && (
            <div className="mb-7" data-testid="contact-open-tasks">
              <div className="flex items-center gap-2 mb-4">
                <h2 style={{ fontFamily: SERIF, fontSize: '26px', color: INK, lineHeight: 1.1 }}>Open tasks</h2>
                <span className="text-xs" style={{ color: FAINT }}>({openTasks.length})</span>
              </div>
              <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${LINE}`, background: '#FFFFFF' }}>
                {openTasks.map((task, idx) => (
                  <div key={task.id} data-testid={`contact-task-${idx}`}
                    className="flex items-start gap-3 p-4"
                    style={{ borderTop: idx > 0 ? `1px solid ${LINE}` : 'none' }}>
                    <button
                      onClick={() => toggleTask(task)}
                      data-testid={`contact-task-checkbox-${idx}`}
                      aria-label="Complete task"
                      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ border: `1.8px solid rgba(33,46,64,.22)`, background: '#fff', transition: 'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(33,46,64,.22)'; }}
                    >
                      <Check size={12} style={{ color: FAINT }} />
                    </button>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openTaskDrawer(task.id, { onChanged: load })} data-testid={`contact-task-open-${idx}`}>
                      <p className="text-sm font-medium hover:underline" style={{ color: INK }}>{task.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: MUTE }}>
                        {task.owner && <span>{task.owner}</span>}
                        {task.due_date && <><span>·</span><span>{task.due_date}</span></>}
                        {task.priority && <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(103,150,194,0.10)', color: BLUE_D, fontWeight: 700 }}>{task.priority}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conversation history */}
          <div className="flex items-center gap-2 mb-4">
            <h2 style={{ fontFamily: SERIF, fontSize: '26px', color: INK, lineHeight: 1.1 }}>
              Conversations
            </h2>
            <span className="text-xs" style={{ color: FAINT }}>({actionCards.length})</span>
          </div>

          {actionCards.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center rounded-3xl"
              style={{ background: '#FFFFFF', border: `1px solid ${LINE}` }}
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(103,150,194,0.10)' }}>
                <MessageSquare size={20} style={{ color: BLUE }} />
              </div>
              <p className="text-sm font-medium mb-1" style={{ color: INK }}>No conversations yet</p>
              <p className="text-xs mb-4" style={{ color: MUTE }}>Action Cards linked to this person will appear here</p>
              <Link to="/capture">
                <button className="text-xs font-semibold px-4 py-2 rounded-full text-white" style={{ background: BLUE, boxShadow: '0 6px 16px rgba(103,150,194,.3)' }}>
                  + Create first Action Card
                </button>
              </Link>
            </div>
          ) : (
            <div
              data-testid="contact-interaction-history"
              className="rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${LINE}`, background: '#FFFFFF' }}
            >
              {actionCards.map((card, idx) => (
                <Link key={card.id} to={`/action-cards/${card.id}`}>
                  <div
                    className="flex items-start gap-4 p-4 group cursor-pointer"
                    style={{ borderTop: idx > 0 ? `1px solid ${LINE}` : 'none', transition: 'background .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = CARD_ALT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; }}
                  >
                    {/* Left: content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold truncate" style={{ color: INK }}>
                          {card.title}
                        </p>
                        <StatusBadge status={card.status} />
                      </div>

                      {card.summary_bullets?.[0] && (
                        <p className="text-xs line-clamp-2 leading-relaxed mb-2" style={{ color: MUTE }}>
                          {card.summary_bullets[0]}
                        </p>
                      )}

                      {card.tags?.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {card.tags.slice(0, 4).map(tag => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(33,46,64,.06)', color: MUTE }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right: date + arrow */}
                    <div className="shrink-0 flex flex-col items-end gap-2">
                      <p className="text-xs whitespace-nowrap" style={{ color: FAINT }}>
                        {new Date(card.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                      <ChevronRight size={14} className="opacity-0 group-hover:opacity-100" style={{ color: BLUE, transition: 'opacity .15s' }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <EmailModal open={emailOpen} onClose={() => { setEmailOpen(false); load(); }} actionCard={recentCard ? { ...recentCard, contact_email: recentCard.contact_email || contact.email || '' } : null} />
    </div>
  );
}
